// src/components/BucksAdminWorkers.jsx
import React, { useState, useEffect, useCallback } from 'react';
import '../stylings/BucksAdminWorkers.css';

function BucksAdminWorkers() {
  // State management
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [allPunchRecords, setAllPunchRecords] = useState([]); // Store all punch records
  const [punchRecords, setPunchRecords] = useState([]); // Filtered punch records for display
  const [dailySummaries, setDailySummaries] = useState([]);
  const [loading, setLoading] = useState({
    workers: true,
    punches: false,
    summaries: false
  });
  const [activeTab, setActiveTab] = useState('punches'); // 'punches' or 'summaries'
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], // Extended to 30 days
    end: new Date().toISOString().split('T')[0]
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    user_id: '',
    punch_type: 'clock_in',
    punch_time: '',
    punch_date: new Date().toISOString().split('T')[0],
    location: 'Main Farm',
    notes: ''
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [punchTypeFilter, setPunchTypeFilter] = useState('all');

  // API Base URL
  const API_BASE_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default';

  // Extract workers from punch records
  const extractWorkersFromPunchRecords = useCallback((punchRecords) => {
    const uniqueWorkers = [];
    const seenUserIds = new Set();
    
    console.log('Extracting workers from punch records:', punchRecords);
    
    punchRecords.forEach(record => {
      console.log(`Processing record: user_id=${record.user_id}, username=${record.username}`);
      if (!seenUserIds.has(record.user_id)) {
        seenUserIds.add(record.user_id);
        const worker = {
          id: record.user_id,
          username: record.username,
          email: `${record.username}@bucksfarmstand.com`, // Placeholder
          role: 'worker', // Default role
          is_active: 1
        };
        console.log('Adding unique worker:', worker);
        uniqueWorkers.push(worker);
      }
    });
    
    console.log('Final unique workers:', uniqueWorkers);
    return uniqueWorkers;
  }, []);

  // Fetch workers list - temporary solution using punch records
  const fetchWorkers = useCallback(async () => {
    setLoading(prev => ({ ...prev, workers: true }));
    try {
      // TODO: Replace with actual workers endpoint when available
      // const response = await fetch(`${API_BASE_URL}/bucks-admin-users-get-all`);
      // if (!response.ok) throw new Error('Failed to fetch workers');
      // const data = await response.json();
      
      // For now, we'll wait for punch records to be loaded first
      // Workers will be extracted in the fetchAllPunchRecords function
      setWorkers([]);
    } catch (error) {
      console.error('Error fetching workers:', error);
      setNotification({
        show: true,
        message: 'Failed to load workers list',
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, workers: false }));
    }
  }, []);

  // Fetch all punch records from API
  const fetchAllPunchRecords = useCallback(async () => {
    setLoading(prev => ({ ...prev, punches: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/bucks-admin-worker-punches-get-all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched punch records:', data);
      setAllPunchRecords(data);
      
      // Extract unique workers from punch records
      const uniqueWorkers = extractWorkersFromPunchRecords(data);
      console.log('Extracted workers:', uniqueWorkers);
      
      if (uniqueWorkers.length > 0) {
        setWorkers(uniqueWorkers);
        // Set the first worker as selected if no worker is currently selected
        setSelectedWorker(prev => {
          if (!prev && uniqueWorkers.length > 0) {
            console.log('Selected first worker:', uniqueWorkers[0]);
            return uniqueWorkers[0];
          }
          return prev;
        });
      }
      
    } catch (error) {
      console.error('Error fetching punch records:', error);
      setNotification({
        show: true,
        message: 'Failed to load punch records from server',
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, punches: false }));
    }
  }, [extractWorkersFromPunchRecords]);

  // Filter punch records based on selected worker and date range
  const filterPunchRecords = useCallback(() => {
    console.log('filterPunchRecords called with:', {
      selectedWorker,
      allPunchRecordsLength: allPunchRecords.length,
      dateRange,
      statusFilter,
      punchTypeFilter
    });

    if (!selectedWorker || allPunchRecords.length === 0) {
      console.log('No worker selected or no records available');
      setPunchRecords([]);
      return;
    }

    const filtered = allPunchRecords.filter(record => {
      // Filter by selected worker
      const workerMatch = record.user_id === selectedWorker.id;
      console.log(`Record ${record.id}: user_id ${record.user_id} === selectedWorker.id ${selectedWorker.id}? ${workerMatch}`);
      
      if (!workerMatch) return false;
      
      // Filter by date range with detailed logging
      const recordDate = new Date(record.punch_date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      console.log(`Date check for record ${record.id}:`, {
        recordDate: record.punch_date,
        startDate: dateRange.start,
        endDate: dateRange.end,
        recordDateObj: recordDate,
        startDateObj: startDate,
        endDateObj: endDate,
        isAfterStart: recordDate >= startDate,
        isBeforeEnd: recordDate <= endDate
      });
      
      const dateMatch = recordDate >= startDate && recordDate <= endDate;
      if (!dateMatch) {
        console.log(`Record ${record.id} filtered out by date range`);
        return false;
      }
      
      // Apply additional filters
      const statusMatch = statusFilter === 'all' || record.status === statusFilter;
      const typeMatch = punchTypeFilter === 'all' || record.punch_type === punchTypeFilter;
      
      console.log(`Record ${record.id} status filter:`, { status: record.status, statusFilter, statusMatch });
      console.log(`Record ${record.id} type filter:`, { punch_type: record.punch_type, punchTypeFilter, typeMatch });
      
      return statusMatch && typeMatch;
    });
    
    console.log(`Filtered ${filtered.length} records from ${allPunchRecords.length} total`);
    console.log('Filtered records:', filtered);
    
    // Sort by date and time (newest first)
    filtered.sort((a, b) => {
      const dateCompare = new Date(b.punch_date) - new Date(a.punch_date);
      if (dateCompare !== 0) return dateCompare;
      return new Date(b.punch_time) - new Date(a.punch_time);
    });
    
    setPunchRecords(filtered);
  }, [selectedWorker, allPunchRecords, dateRange, statusFilter, punchTypeFilter]);

  // Fetch daily summaries for selected worker
  const fetchDailySummaries = useCallback(async (workerId) => {
    if (!workerId) return;
    
    setLoading(prev => ({ ...prev, summaries: true }));
    try {
      // TODO: Replace with actual daily summaries endpoint when available
      // const response = await fetch(
      //   `${API_BASE_URL}/bucks-admin-worker-summaries?user_id=${workerId}&start=${dateRange.start}&end=${dateRange.end}`
      // );
      // if (!response.ok) throw new Error('Failed to fetch daily summaries');
      // const data = await response.json();
      // setDailySummaries(data);
      
      // Mock data for development - replace when daily summaries API is available
      const mockSummaries = [
        {
          id: 1,
          user_id: workerId,
          work_date: '2025-06-30',
          clock_in_time: '08:00:00',
          clock_out_time: '17:00:00',
          total_hours: 8.5,
          total_break_minutes: 30,
          break_count: 1,
          regular_hours: 8.0,
          overtime_hours: 0.5,
          status: 'complete',
          notes: ''
        },
        {
          id: 2,
          user_id: workerId,
          work_date: '2025-06-29',
          clock_in_time: '08:15:00',
          clock_out_time: '16:45:00',
          total_hours: 8.0,
          total_break_minutes: 30,
          break_count: 1,
          regular_hours: 8.0,
          overtime_hours: 0.0,
          status: 'complete',
          notes: 'Left 15 minutes early'
        }
      ];
      setDailySummaries(mockSummaries);
    } catch (error) {
      console.error('Error fetching daily summaries:', error);
      setNotification({
        show: true,
        message: 'Failed to load daily summaries',
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, summaries: false }));
    }
  }, [dateRange.start, dateRange.end]);

  // Handle worker selection
  const handleWorkerSelect = (worker) => {
    setSelectedWorker(worker);
    setSearchTerm('');
  };

  // Handle manual punch entry
  const handleManualPunchSubmit = async (e) => {
    e.preventDefault();
    try {
      // TODO: Replace with actual manual punch entry endpoint when available
      const response = await fetch(`${API_BASE_URL}/bucks-admin-worker-punch-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...manualEntryForm,
          punch_time: `${manualEntryForm.punch_date}T${manualEntryForm.punch_time}:00`
        })
      });
      
      if (!response.ok) throw new Error('Failed to create manual punch entry');
      
      setNotification({
        show: true,
        message: 'Manual punch entry created successfully',
        type: 'success'
      });
      
      setShowManualEntry(false);
      setManualEntryForm({
        user_id: selectedWorker?.id || '',
        punch_type: 'clock_in',
        punch_time: '',
        punch_date: new Date().toISOString().split('T')[0],
        location: 'Main Farm',
        notes: ''
      });
      
      // Refresh punch records
      fetchAllPunchRecords();
    } catch (error) {
      console.error('Error creating manual punch entry:', error);
      setNotification({
        show: true,
        message: 'Failed to create manual punch entry - endpoint not available yet',
        type: 'error'
      });
    }
  };

  // Utility functions
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatHours = (hours) => {
    if (!hours) return '0.00';
    return parseFloat(hours).toFixed(2);
  };

  const getPunchTypeDisplay = (type) => {
    const typeMap = {
      'clock_in': 'Clock In',
      'clock_out': 'Clock Out',
      'break_start': 'Break Start',
      'break_end': 'Break End'
    };
    return typeMap[type] || type;
  };

  const getPunchTypeClass = (type) => {
    const classMap = {
      'clock_in': 'punch-type-in',
      'clock_out': 'punch-type-out',
      'break_start': 'punch-type-break-start',
      'break_end': 'punch-type-break-end'
    };
    return classMap[type] || '';
  };

  const getStatusClass = (status) => {
    const classMap = {
      'complete': 'status-complete',
      'incomplete': 'status-incomplete',
      'edited': 'status-edited',
      'active': 'status-active',
      'deleted': 'status-deleted'
    };
    return classMap[status] || '';
  };

  // Filter workers based on search term
  const filteredWorkers = workers.filter(worker => 
    worker.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (worker.email && worker.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Effects
  useEffect(() => {
    // Load workers first, then punch records
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    // Load punch records after component mounts
    fetchAllPunchRecords();
  }, [fetchAllPunchRecords]);

  useEffect(() => {
    // Filter records when data is available or filters change
    if (allPunchRecords.length > 0) {
      console.log('Filtering records with:', {
        selectedWorker: selectedWorker?.username,
        recordsCount: allPunchRecords.length,
        dateRange,
        statusFilter,
        punchTypeFilter
      });
      filterPunchRecords();
    }
  }, [allPunchRecords, selectedWorker, dateRange, statusFilter, punchTypeFilter, filterPunchRecords]);

  useEffect(() => {
    if (selectedWorker && activeTab === 'summaries') {
      fetchDailySummaries(selectedWorker.id);
    }
  }, [selectedWorker, activeTab, dateRange, fetchDailySummaries]);

  useEffect(() => {
    if (manualEntryForm.user_id === '' && selectedWorker) {
      setManualEntryForm(prev => ({ ...prev, user_id: selectedWorker.id }));
    }
  }, [selectedWorker, manualEntryForm.user_id]);

  // Auto-dismiss notifications
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  return (
    <div className="workers-section">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Workers Selection and Controls */}
      <div className="workers-controls">
        <div className="workers-selection">
          <div className="worker-selector">
            <label>Select Worker:</label>
            <div className="worker-search-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search workers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="worker-search-input"
              />
            </div>
            <select 
              value={selectedWorker?.id || ''} 
              onChange={(e) => {
                const worker = workers.find(w => w.id === parseInt(e.target.value));
                handleWorkerSelect(worker);
              }}
              className="worker-select"
            >
              <option value="">Select a worker...</option>
              {filteredWorkers.map(worker => (
                <option key={worker.id} value={worker.id}>
                  {worker.username} ({worker.role})
                  {!worker.is_active && ' - Inactive'}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="date-range-container">
          <label>Date Range:</label>
          <div className="date-inputs">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="date-input"
            />
            <span>to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="date-input"
            />
          </div>
        </div>

        <div className="action-buttons">
          <button 
            className="manual-entry-btn"
            onClick={() => setShowManualEntry(true)}
            disabled={!selectedWorker}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            Manual Entry
          </button>
        </div>
      </div>

      {/* Worker Info Card */}
      {selectedWorker && (
        <div className="worker-info-card">
          <div className="worker-avatar">
            <span>{selectedWorker.username.charAt(0).toUpperCase()}</span>
          </div>
          <div className="worker-details">
            <h3>{selectedWorker.username}</h3>
            <p>{selectedWorker.email || `${selectedWorker.username}@bucksfarmstand.com`}</p>
            <span className={`role-badge role-${selectedWorker.role}`}>
              {selectedWorker.role.charAt(0).toUpperCase() + selectedWorker.role.slice(1)}
            </span>
            <span className={`status-badge ${selectedWorker.is_active ? 'status-active' : 'status-inactive'}`}>
              {selectedWorker.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="worker-stats">
            <div className="worker-stat-item">
              <span className="stat-label">Records in range:</span>
              <span className="stat-value">{punchRecords.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'punches' ? 'active' : ''}`}
          onClick={() => setActiveTab('punches')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          Punch Records ({punchRecords.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'summaries' ? 'active' : ''}`}
          onClick={() => setActiveTab('summaries')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          Daily Summaries
        </button>
      </div>

      {/* Punch Records Tab */}
      {activeTab === 'punches' && (
        <div className="punches-tab">
          {/* Filters */}
          <div className="filters-container">
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="edited">Edited</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Punch Type:</label>
              <select 
                value={punchTypeFilter} 
                onChange={(e) => setPunchTypeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Types</option>
                <option value="clock_in">Clock In</option>
                <option value="clock_out">Clock Out</option>
                <option value="break_start">Break Start</option>
                <option value="break_end">Break End</option>
              </select>
            </div>
          </div>

          {/* Punch Records Table */}
          <div className="table-container">
            {loading.punches ? (
              <div className="loading-container">
                <span className="loading-spinner"></span>
                Loading punch records...
              </div>
            ) : (
              <table className="workers-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Notes</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {punchRecords.length > 0 ? (
                    punchRecords.map(punch => (
                      <tr key={punch.id}>
                        <td>{formatDate(punch.punch_date)}</td>
                        <td>{formatTime(punch.punch_time)}</td>
                        <td>
                          <span className={`punch-type-badge ${getPunchTypeClass(punch.punch_type)}`}>
                            {getPunchTypeDisplay(punch.punch_type)}
                          </span>
                        </td>
                        <td>{punch.location || 'N/A'}</td>
                        <td className="notes-cell">
                          {punch.notes || '-'}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(punch.status)}`}>
                            {punch.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="edit-btn" title="Edit punch">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-results">
                        No punch records found for the selected criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Daily Summaries Tab */}
      {activeTab === 'summaries' && (
        <div className="summaries-tab">
          <div className="table-container">
            {loading.summaries ? (
              <div className="loading-container">
                <span className="loading-spinner"></span>
                Loading daily summaries...
              </div>
            ) : (
              <table className="workers-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Total Hours</th>
                    <th>Regular</th>
                    <th>Overtime</th>
                    <th>Breaks</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {dailySummaries.length > 0 ? (
                    dailySummaries.map(summary => (
                      <tr key={summary.id}>
                        <td>{formatDate(summary.work_date)}</td>
                        <td>{summary.clock_in_time || 'N/A'}</td>
                        <td>{summary.clock_out_time || 'N/A'}</td>
                        <td className="hours-cell">{formatHours(summary.total_hours)} hrs</td>
                        <td className="hours-cell">{formatHours(summary.regular_hours)} hrs</td>
                        <td className="hours-cell overtime">{formatHours(summary.overtime_hours)} hrs</td>
                        <td>
                          {summary.break_count} ({summary.total_break_minutes || 0} min)
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(summary.status)}`}>
                            {summary.status}
                          </span>
                        </td>
                        <td className="notes-cell">
                          {summary.notes || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="no-results">
                        No daily summaries found for the selected date range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Summary Stats */}
          {dailySummaries.length > 0 && (
            <div className="summary-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className="stat-content">
                  <h4>Total Hours</h4>
                  <p>{formatHours(dailySummaries.reduce((sum, s) => sum + (s.total_hours || 0), 0))}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon overtime">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                  </svg>
                </div>
                <div className="stat-content">
                  <h4>Overtime Hours</h4>
                  <p>{formatHours(dailySummaries.reduce((sum, s) => sum + (s.overtime_hours || 0), 0))}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12V7a1 1 0 0 1 1-1h4l2 2h4a1 1 0 0 1 1 1v4"></path>
                    <circle cx="12" cy="18" r="3"></circle>
                  </svg>
                </div>
                <div className="stat-content">
                  <h4>Days Worked</h4>
                  <p>{dailySummaries.filter(s => s.status === 'complete').length}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Manual Punch Entry</h3>
              <button 
                className="close-modal"
                onClick={() => setShowManualEntry(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleManualPunchSubmit} className="manual-entry-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Worker:</label>
                  <select 
                    value={manualEntryForm.user_id} 
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, user_id: e.target.value }))}
                    required
                  >
                    <option value="">Select worker...</option>
                    {workers.filter(w => w.is_active).map(worker => (
                      <option key={worker.id} value={worker.id}>
                        {worker.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Punch Type:</label>
                  <select 
                    value={manualEntryForm.punch_type} 
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, punch_type: e.target.value }))}
                    required
                  >
                    <option value="clock_in">Clock In</option>
                    <option value="clock_out">Clock Out</option>
                    <option value="break_start">Break Start</option>
                    <option value="break_end">Break End</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date:</label>
                  <input
                    type="date"
                    value={manualEntryForm.punch_date}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, punch_date: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Time:</label>
                  <input
                    type="time"
                    value={manualEntryForm.punch_time}
                    onChange={(e) => setManualEntryForm(prev => ({ ...prev, punch_time: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  value={manualEntryForm.location}
                  onChange={(e) => setManualEntryForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Main Farm, Greenhouse, Storage"
                />
              </div>
              <div className="form-group">
                <label>Notes:</label>
                <textarea
                  value={manualEntryForm.notes}
                  onChange={(e) => setManualEntryForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes..."
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowManualEntry(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Create Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BucksAdminWorkers;